import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  Unsubscribe
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { Conversation, Message, MessagingSettings, MessageChannel, MessagingActivationRequest, ActivationRequestStatus } from '../types';

const CONVERSATIONS_COLLECTION = 'conversations';
const MESSAGES_COLLECTION = 'messages';
const MESSAGING_SETTINGS_COLLECTION = 'messagingSettings';
const ACTIVATION_REQUESTS_COLLECTION = 'messagingActivationRequests';

// ==================== HELPERS ====================

/**
 * Get the effective message status (backward compat with legacy twilioStatus)
 */
export const getMessageStatus = (message: Message): string => {
  return message.status || message.twilioStatus || 'queued';
};

/**
 * Get the effective error message (backward compat)
 */
export const getMessageError = (message: Message): string | undefined => {
  return message.errorMessage || message.twilioErrorMessage;
};

/**
 * Apply backward compat defaults when reading conversation from Firestore
 */
const normalizeConversation = (id: string, data: any): Conversation => ({
  ...data,
  id,
  channel: data.channel || 'whatsapp',
});

/**
 * Apply backward compat defaults when reading message from Firestore
 */
const normalizeMessage = (id: string, data: any): Message => ({
  ...data,
  id,
  channel: data.channel || 'whatsapp',
});

// ==================== CONVERSATION OPERATIONS ====================

/**
 * Get all conversations for an organization
 */
export const getConversationsByOrganization = async (
  organizationId: string,
  status: 'active' | 'archived' | 'all' = 'active'
): Promise<Conversation[]> => {
  try {
    let q = query(
      collection(db, CONVERSATIONS_COLLECTION),
      where('organizationId', '==', organizationId),
      orderBy('lastMessageAt', 'desc')
    );

    if (status !== 'all') {
      q = query(
        collection(db, CONVERSATIONS_COLLECTION),
        where('organizationId', '==', organizationId),
        where('status', '==', status),
        orderBy('lastMessageAt', 'desc')
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => normalizeConversation(d.id, d.data()));
  } catch (error) {
    console.error('Error getting conversations:', error);
    throw error;
  }
};

/**
 * Get a single conversation by ID
 */
export const getConversationById = async (conversationId: string): Promise<Conversation | null> => {
  try {
    const docRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return normalizeConversation(docSnap.id, docSnap.data());
  } catch (error) {
    console.error('Error getting conversation:', error);
    throw error;
  }
};

/**
 * Archive a conversation
 */
export const archiveConversation = async (conversationId: string): Promise<void> => {
  try {
    const docRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    await updateDoc(docRef, {
      status: 'archived',
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error archiving conversation:', error);
    throw error;
  }
};

/**
 * Unarchive a conversation
 */
export const unarchiveConversation = async (conversationId: string): Promise<void> => {
  try {
    const docRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    await updateDoc(docRef, {
      status: 'active',
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error unarchiving conversation:', error);
    throw error;
  }
};

/**
 * Subscribe to conversations (real-time updates) with optional channel filter
 */
export const subscribeToConversations = (
  organizationId: string,
  callback: (conversations: Conversation[]) => void,
  status: 'active' | 'archived' = 'active',
  channel?: MessageChannel
): Unsubscribe => {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('organizationId', '==', organizationId),
    where('status', '==', status),
    orderBy('lastMessageAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    let conversations = snapshot.docs.map(d => normalizeConversation(d.id, d.data()));
    if (channel) {
      conversations = conversations.filter(c => c.channel === channel);
    }
    callback(conversations);
  }, (error) => {
    console.error('Error in conversations subscription:', error);
  });
};

/**
 * Subscribe to total unread count
 */
export const subscribeToUnreadCount = (
  organizationId: string,
  callback: (count: number) => void
): Unsubscribe => {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION),
    where('organizationId', '==', organizationId),
    where('status', '==', 'active')
  );

  return onSnapshot(q, (snapshot) => {
    const totalUnread = snapshot.docs.reduce((sum, doc) => {
      const data = doc.data();
      return sum + (data.unreadCount || 0);
    }, 0);
    callback(totalUnread);
  }, (error) => {
    console.error('Error in unread count subscription:', error);
  });
};

// ==================== MESSAGE OPERATIONS ====================

/**
 * Get messages for a conversation
 */
export const getMessagesByConversation = async (
  conversationId: string,
  messageLimit: number = 50
): Promise<Message[]> => {
  try {
    const q = query(
      collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_COLLECTION),
      orderBy('sentAt', 'desc'),
      limit(messageLimit)
    );

    const snapshot = await getDocs(q);
    const messages = snapshot.docs.map(d => normalizeMessage(d.id, d.data()));

    // Return in chronological order
    return messages.reverse();
  } catch (error) {
    console.error('Error getting messages:', error);
    throw error;
  }
};

/**
 * Subscribe to messages (real-time updates)
 */
export const subscribeToMessages = (
  conversationId: string,
  callback: (messages: Message[]) => void,
  messageLimit: number = 100
): Unsubscribe => {
  const q = query(
    collection(db, CONVERSATIONS_COLLECTION, conversationId, MESSAGES_COLLECTION),
    orderBy('sentAt', 'desc'),
    limit(messageLimit)
  );

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map(d => normalizeMessage(d.id, d.data()));

    // Return in chronological order
    callback(messages.reverse());
  }, (error) => {
    console.error('Error in messages subscription:', error);
  });
};

/**
 * Send a message via the appropriate Cloud Function based on channel
 */
export const sendMessage = async (
  conversationId: string,
  body: string,
  organizationId: string,
  channel: MessageChannel = 'whatsapp',
  options?: { subject?: string; htmlBody?: string }
): Promise<{ success: boolean; messageId?: string; error?: string }> => {
  try {
    const functionName = channel === 'sms'
      ? 'sendSmsMessage'
      : channel === 'email'
      ? 'sendEmailMessage'
      : 'sendWhatsAppMessage';

    const callable = httpsCallable(functions, functionName);
    const payload: any = {
      conversationId,
      body,
      organizationId
    };

    if (channel === 'email' && options) {
      if (options.subject) payload.subject = options.subject;
      if (options.htmlBody) payload.htmlBody = options.htmlBody;
    }

    const result = await callable(payload);
    return result.data as { success: boolean; messageId?: string };
  } catch (error: any) {
    console.error('Error sending message:', error);
    return {
      success: false,
      error: error.message || 'Failed to send message'
    };
  }
};

/**
 * Mark conversation as read via Cloud Function
 */
export const markAsRead = async (conversationId: string): Promise<void> => {
  try {
    const markConversationRead = httpsCallable(functions, 'markConversationRead');
    await markConversationRead({ conversationId });
  } catch (error) {
    console.error('Error marking as read:', error);
    throw error;
  }
};

// ==================== SETTINGS OPERATIONS ====================

/**
 * Get messaging settings for an organization (with backward compat)
 */
export const getMessagingSettings = async (
  organizationId: string
): Promise<MessagingSettings | null> => {
  try {
    const docRef = doc(db, MESSAGING_SETTINGS_COLLECTION, organizationId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const data = docSnap.data();

    // Backward compat: migrate legacy `enabled` to `whatsappEnabled`
    return {
      id: docSnap.id,
      ...data,
      whatsappEnabled: data.whatsappEnabled ?? data.enabled ?? false,
      smsEnabled: data.smsEnabled ?? false,
      emailEnabled: data.emailEnabled ?? false,
    } as MessagingSettings;
  } catch (error) {
    console.error('Error getting messaging settings:', error);
    throw error;
  }
};

/**
 * Update messaging settings
 */
export const updateMessagingSettings = async (
  organizationId: string,
  settings: Partial<MessagingSettings>
): Promise<void> => {
  try {
    const docRef = doc(db, MESSAGING_SETTINGS_COLLECTION, organizationId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      await setDoc(docRef, {
        ...settings,
        id: organizationId,
        organizationId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
    } else {
      await updateDoc(docRef, {
        ...settings,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Error updating messaging settings:', error);
    throw error;
  }
};

// ==================== CONTACTABLE USERS ====================

export interface ContactableUser {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  type: 'player' | 'guardian';
}

/**
 * Get users contactable by a given channel
 */
export const getContactableUsers = async (
  organizationId: string,
  channel: MessageChannel,
  userType?: 'player' | 'guardian'
): Promise<ContactableUser[]> => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users: ContactableUser[] = [];

    usersSnapshot.docs.forEach(d => {
      const data = d.data();

      const hasOrgRole = data.roles?.some((role: { organizationId: string }) =>
        role.organizationId === organizationId
      );
      if (!hasOrgRole) return;

      // Determine user type
      const isPlayer = data.roles?.some((role: { role: string[] }) =>
        role.role?.includes('player')
      );
      const isGuardian = data.roles?.some((role: { role: string[] }) =>
        role.role?.includes('guardian')
      );
      const type = isPlayer ? 'player' : isGuardian ? 'guardian' : null;
      if (!type) return;
      if (userType && type !== userType) return;

      // Filter by channel capability
      if (channel === 'email') {
        const email = data.email;
        if (!email) return;
        users.push({ id: d.id, name: data.name || data.email || 'Unknown', email, type });
      } else {
        // WhatsApp or SMS need a phone number
        const phone = data.whatsappPhone || data.phone;
        if (!phone) return;
        users.push({ id: d.id, name: data.name || data.email || 'Unknown', phone, type });
      }
    });

    return users;
  } catch (error) {
    console.error('Error getting contactable users:', error);
    throw error;
  }
};

/**
 * Legacy wrapper — get users with WhatsApp-capable phone numbers
 */
export const getUsersWithWhatsApp = async (
  organizationId: string,
  userType?: 'player' | 'guardian'
): Promise<{ id: string; name: string; phone: string; type: 'player' | 'guardian' }[]> => {
  const users = await getContactableUsers(organizationId, 'whatsapp', userType);
  return users.map(u => ({ id: u.id, name: u.name, phone: u.phone || '', type: u.type }));
};

// ==================== PHONE HELPERS ====================

/**
 * Normalize phone number to a consistent format for matching
 */
const normalizePhoneNumber = (phone: string): string => {
  let normalized = phone.replace(/[^\d+]/g, '');
  if (normalized.startsWith('+')) {
    normalized = normalized.substring(1);
  }
  if (normalized.startsWith('0') && normalized.length === 10) {
    normalized = '254' + normalized.substring(1);
  }
  return normalized;
};

// ==================== FIND OR CREATE CONVERSATION ====================

/**
 * Find or create a conversation with a user (multi-channel)
 */
export const findOrCreateConversation = async (
  organizationId: string,
  userId: string,
  userName: string,
  userType: 'player' | 'guardian',
  channel: MessageChannel = 'whatsapp',
  contactInfo: { phone?: string; email?: string } = {}
): Promise<Conversation> => {
  try {
    const qByOrg = query(
      collection(db, CONVERSATIONS_COLLECTION),
      where('organizationId', '==', organizationId),
      where('status', '==', 'active')
    );

    const allConversations = await getDocs(qByOrg);

    if (channel === 'email' && contactInfo.email) {
      // Dedup by email + channel
      const existingByEmail = allConversations.docs.find(d => {
        const data = d.data();
        return (data.channel || 'whatsapp') === 'email' && data.participantEmail === contactInfo.email;
      });

      if (existingByEmail) {
        const existingData = existingByEmail.data();
        const userRef = doc(db, 'users', userId);
        await updateDoc(doc(db, CONVERSATIONS_COLLECTION, existingByEmail.id), {
          participantUserId: userId,
          participantName: userName,
          participantType: userType,
          participantUserRef: userRef,
          updatedAt: Timestamp.now()
        });
        return normalizeConversation(existingByEmail.id, {
          ...existingData,
          participantUserId: userId,
          participantName: userName,
          participantType: userType
        });
      }
    } else if (contactInfo.phone) {
      // Dedup by phone + channel for WhatsApp/SMS
      const normalizedPhone = normalizePhoneNumber(contactInfo.phone);
      const existingByPhone = allConversations.docs.find(d => {
        const data = d.data();
        const docChannel = data.channel || 'whatsapp';
        if (docChannel !== channel) return false;
        const existingPhone = normalizePhoneNumber(data.participantPhone || '');
        return existingPhone === normalizedPhone;
      });

      if (existingByPhone) {
        const existingData = existingByPhone.data();
        const userRef = doc(db, 'users', userId);
        await updateDoc(doc(db, CONVERSATIONS_COLLECTION, existingByPhone.id), {
          participantUserId: userId,
          participantName: userName,
          participantType: userType,
          participantUserRef: userRef,
          updatedAt: Timestamp.now()
        });
        return normalizeConversation(existingByPhone.id, {
          ...existingData,
          participantUserId: userId,
          participantName: userName,
          participantType: userType
        });
      }
    }

    // Check by userId + channel
    const matchByUser = allConversations.docs.find(d => {
      const data = d.data();
      return data.participantUserId === userId && (data.channel || 'whatsapp') === channel;
    });

    if (matchByUser) {
      return normalizeConversation(matchByUser.id, matchByUser.data());
    }

    // Create new conversation
    const userRef = doc(db, 'users', userId);
    const now = Timestamp.now();

    const newConversation: Omit<Conversation, 'id'> = {
      organizationId,
      channel,
      participantUserId: userId,
      participantName: userName,
      participantPhone: contactInfo.phone,
      participantEmail: contactInfo.email,
      participantType: userType,
      participantUserRef: userRef,
      status: 'active',
      lastMessageAt: now,
      lastMessagePreview: '',
      lastMessageDirection: 'outbound',
      unreadCount: 0,
      sessionActive: false,
      createdAt: now,
      updatedAt: now
    };

    // Remove undefined fields
    const cleanData = Object.fromEntries(
      Object.entries(newConversation).filter(([_, v]) => v !== undefined)
    );

    const docRef = await addDoc(collection(db, CONVERSATIONS_COLLECTION), cleanData);

    return {
      id: docRef.id,
      ...newConversation
    } as Conversation;
  } catch (error) {
    console.error('Error finding/creating conversation:', error);
    throw error;
  }
};

// ==================== SETTINGS SUBSCRIPTION ====================

/**
 * Subscribe to messaging settings (real-time updates)
 */
export const subscribeToMessagingSettings = (
  organizationId: string,
  callback: (settings: MessagingSettings | null) => void
): Unsubscribe => {
  const docRef = doc(db, MESSAGING_SETTINGS_COLLECTION, organizationId);

  return onSnapshot(docRef, (docSnap) => {
    if (!docSnap.exists()) {
      callback(null);
      return;
    }
    const data = docSnap.data();
    callback({
      id: docSnap.id,
      ...data,
      whatsappEnabled: data.whatsappEnabled ?? data.enabled ?? false,
      smsEnabled: data.smsEnabled ?? false,
      emailEnabled: data.emailEnabled ?? false,
    } as MessagingSettings);
  }, (error) => {
    console.error('Error in messaging settings subscription:', error);
  });
};

// ==================== ACTIVATION REQUESTS ====================

/**
 * Create a messaging activation request for an organization
 */
export const createActivationRequest = async (
  orgId: string,
  orgName: string,
  requestedBy: { userId: string; name: string },
  note?: string
): Promise<string> => {
  try {
    const now = Timestamp.now();
    const data: Record<string, any> = {
      organizationId: orgId,
      organizationName: orgName,
      status: 'pending' as ActivationRequestStatus,
      requestedBy,
      createdAt: now,
      updatedAt: now
    };
    if (note) data.note = note;

    const docRef = await addDoc(collection(db, ACTIVATION_REQUESTS_COLLECTION), data);
    return docRef.id;
  } catch (error) {
    console.error('Error creating activation request:', error);
    throw error;
  }
};

/**
 * Get the latest activation request for an organization
 */
export const getActivationRequest = async (
  orgId: string
): Promise<MessagingActivationRequest | null> => {
  try {
    const q = query(
      collection(db, ACTIVATION_REQUESTS_COLLECTION),
      where('organizationId', '==', orgId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const d = snapshot.docs[0];
    return { id: d.id, ...d.data() } as MessagingActivationRequest;
  } catch (error) {
    console.error('Error getting activation request:', error);
    throw error;
  }
};

/**
 * Subscribe to the latest activation request for an organization (real-time)
 */
export const subscribeToActivationRequest = (
  orgId: string,
  callback: (request: MessagingActivationRequest | null) => void
): Unsubscribe => {
  const q = query(
    collection(db, ACTIVATION_REQUESTS_COLLECTION),
    where('organizationId', '==', orgId),
    orderBy('createdAt', 'desc'),
    limit(1)
  );

  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }
    const d = snapshot.docs[0];
    callback({ id: d.id, ...d.data() } as MessagingActivationRequest);
  }, (error) => {
    console.error('Error in activation request subscription:', error);
  });
};

// ==================== BULK MESSAGING ====================

/**
 * Send a bulk message via Cloud Function
 */
export const sendBulkMessage = async (
  organizationId: string,
  channel: MessageChannel,
  recipientIds: string[],
  body: string,
  options?: { subject?: string }
): Promise<{ success: boolean; bulkMessageId?: string; error?: string }> => {
  try {
    const callable = httpsCallable(functions, 'sendBulkMessage');
    const payload: any = {
      organizationId,
      channel,
      recipientIds,
      body,
    };
    if (options?.subject) payload.subject = options.subject;

    const result = await callable(payload);
    return result.data as { success: boolean; bulkMessageId?: string };
  } catch (error: any) {
    console.error('Error sending bulk message:', error);
    return {
      success: false,
      error: error.message || 'Failed to send bulk message'
    };
  }
};
